import { useEffect, useId, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import styles from "./WaveformPlayer.module.css";

export interface WaveformPlayerProps {
  /** URL the audio bytes load from — a persisted API path or a staged `blob:` URL. */
  src: string;
  /** File name, used for the accessible label on the controls. */
  name: string;
  /** Compact variant for the list view: shorter waveform, no time read-out. */
  compact?: boolean;
}

/** Reads a resolved colour off the document root — wavesurfer paints to a
 *  canvas, which cannot resolve `var(--token)`, so the value is passed literally. */
function cssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/** mm:ss for a duration in seconds; "0:00" for anything not yet known. */
function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

/**
 * Inline audio player for an attachment, drawn as a wavesurfer.js waveform with
 * a play/pause control and a running time read-out.
 *
 * The waveform is seekable — clicking it jumps playback — so the whole clip is
 * scrubbable from the attachment tab without leaving the job card. The instance
 * is torn down on unmount (and rebuilt if `src` changes), which releases the
 * decoded audio and, for a staged file, lets its `blob:` URL be revoked.
 */
export function WaveformPlayer({ src, name, compact = false }: WaveformPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const labelId = useId();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    setReady(false);
    setPlaying(false);
    setFailed(false);
    setCurrentTime(0);
    setDuration(0);

    const waveSurfer = WaveSurfer.create({
      container,
      url: src,
      height: compact ? 28 : 44,
      waveColor: cssVar("--file-audio", "#6f4a7a"),
      progressColor: cssVar("--accent", "#146b5d"),
      cursorColor: cssVar("--text-2", "#59635f"),
      cursorWidth: 1,
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      normalize: true,
    });
    waveSurferRef.current = waveSurfer;

    waveSurfer.on("ready", () => {
      setReady(true);
      setDuration(waveSurfer.getDuration());
    });
    waveSurfer.on("timeupdate", (time) => setCurrentTime(time));
    waveSurfer.on("play", () => setPlaying(true));
    waveSurfer.on("pause", () => setPlaying(false));
    waveSurfer.on("finish", () => setPlaying(false));
    waveSurfer.on("error", () => setFailed(true));

    return () => {
      waveSurferRef.current = null;
      // Guard: destroy() aborts the in-flight fetch, which can reject the load
      // promise; swallowing keeps that expected abort out of the console.
      try {
        waveSurfer.destroy();
      } catch {
        /* already torn down */
      }
    };
  }, [src, compact]);

  const togglePlay = (): void => {
    waveSurferRef.current?.playPause();
  };

  if (failed) {
    return (
      <div className={`${styles.root} ${compact ? styles.compact : ""}`}>
        <p className={styles.error}>Audio unavailable</p>
      </div>
    );
  }

  return (
    <div className={`${styles.root} ${compact ? styles.compact : ""}`}>
      <button
        type="button"
        className={styles.playButton}
        onClick={togglePlay}
        disabled={!ready}
        aria-labelledby={labelId}
        aria-pressed={playing}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
        <span id={labelId} className={styles.srOnly}>
          {playing ? `Pause ${name}` : `Play ${name}`}
        </span>
      </button>

      <div className={styles.waveWrap}>
        <div ref={containerRef} className={styles.wave} />
        {!ready && <span className={styles.loading}>Loading…</span>}
      </div>

      {!compact && (
        <span className={styles.time} aria-hidden="true">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      )}
    </div>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M4.5 3.2v9.6a.6.6 0 0 0 .92.5l7.3-4.8a.6.6 0 0 0 0-1l-7.3-4.8a.6.6 0 0 0-.92.5Z" fill="currentColor" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path d="M4.5 3h2.2v10H4.5zM9.3 3h2.2v10H9.3z" fill="currentColor" />
    </svg>
  );
}
